import React, { Component } from 'react';
import { connect } from 'react-redux';
import { isEmpty, get, isEqual } from 'lodash';
import asyncRoute from '../../helpers/hoc/asyncRoute';
import { constants } from '../../constants/configuration';
import { dineInModes, extractWhitelabelSubdomain } from '../../utils/helpers';
import { restaurantEntityTypes, serverErrorCodes } from '../../constants/globals';
import {
  selectRestaurant,
  getErrorTitle,
  getErrorDescription,
  getButtonHandlerCta,
} from '../../selectors';
import routes from '../../routes';
import {
  fetchRestaurantMenuAction,
  clearSelectedTable,
  dispatchPreviousSelectedTableAction,
  dispatchVerifiedLocalitiesAction,
  dispatchSelectedAddressAction,
  clearApiErrorResponseAction,
  dispatchUserCartAction,
} from '../../actions/app.action';
import './WelcomeScreen.scss';

const ImageHybrid = asyncRoute(() => import('../../components/ImageHybrid'));
const ScanQR = asyncRoute(() => import('../../components/ScanQR'));
const RestaurantSelectionScreen = asyncRoute(() => import('./RestaurantSelectionScreen'));
const RestaurantWelcomeScreen = asyncRoute(() => import('./RestaurantWelcomeScreen'));
const OrderModePopup = asyncRoute(() => import('../../components/Popup/OrderMode'));
const ExceptionComponent = asyncRoute(() => import('../../components/ExceptionComponent'));

const welcomeScreenConfig = constants.welcome;

class WelcomeScreen extends Component {
  constructor(props) {
    super(props);
    this.state = {
      includeMenu: false,
      showScanQRComponent: false,
      showRestaurantSelectionScreen: false,
      showRestaurantWelcomeScreen: false,
      showOrderModePopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
      showErrorPopup: false,
      activeRestaurantEntityType: '',
    };
  }

  componentWillMount() {
    const { match } = this.props;
    const { includeMenu } = this.state;
    const restaurantIdAndTableId = get(match, 'params.restaurantAndTableId', '');
    if (!isEmpty(restaurantIdAndTableId)) {
      this.props.fetchRestaurantMenuAction(restaurantIdAndTableId, includeMenu).then((response) => {
        this.switchRestaurantEntityType(response);
      });
    }
    this.props.clearSelectedTable();
    this.props.dispatchPreviousSelectedTableAction();
    this.props.dispatchVerifiedLocalitiesAction();
    this.props.dispatchSelectedAddressAction();
  }

  componentDidMount() {
    const { restaurantGroup } = this.props;
    if (!isEmpty(restaurantGroup)) {
      this.setState({
        showRestaurantSelectionScreen: true,
      });
    } else {
      this.setState({
        showRestaurantWelcomeScreen: true,
      });
    }
    // clear user's cart
    this.props.dispatchUserCart({});
  }

  componentWillReceiveProps(nextProps) {
    if (
      !isEmpty(get(nextProps, 'errorResponse')) &&
      !isEqual(get(this.props, 'errorResponse'), get(nextProps, 'errorResponse'))
    ) {
      const { code, message } = nextProps.errorResponse;
      // Suppress API error if the webapp has been visited from a white-label subdomain as they have been custom-handled in ScanQR component
      const subDomain = extractWhitelabelSubdomain();
      if (!(subDomain && code === serverErrorCodes.invalidMethodParams)) {
        this.setState(
          {
            showErrorPopup: true,
            showOrderModePopup: false,
            errorContent: {
              title: getErrorTitle({ source: welcomeScreenConfig, code }),
              description: getErrorDescription({ source: welcomeScreenConfig, code, message }),
              buttonCta: getButtonHandlerCta({ source: welcomeScreenConfig, code }),
            },
          },
          () => {
            this.props.clearApiErrorResponseAction();
          },
        );
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Switch to the appropriate restaurant entity screen as per the updated activeRestaurantEntityType state
    if (
      this.state.activeRestaurantEntityType.length > 0 &&
      this.state.activeRestaurantEntityType !== prevState.activeRestaurantEntityType
    ) {
      const isRestaurantGroup =
        this.state.activeRestaurantEntityType === restaurantEntityTypes.restaurantGroup;

      this.setState({
        showRestaurantSelectionScreen: isRestaurantGroup,
        showRestaurantWelcomeScreen: !isRestaurantGroup,
      });
    }
  }

  switchRestaurantEntityType = (fetchRestaurantMenuResponse) => {
    if (fetchRestaurantMenuResponse) {
      const entityType =
        'restaurantGroup' in fetchRestaurantMenuResponse
          ? restaurantEntityTypes.restaurantGroup
          : restaurantEntityTypes.restaurant;
      this.setState({ activeRestaurantEntityType: entityType });
    }
  };

  handleSwitchServicesClick = (scanCode) => {
    const { includeMenu } = this.state;
    this.setState(
      {
        showOrderModePopup: true,
      },
      () => {
        this.props.fetchRestaurantMenuAction(scanCode, includeMenu).then((response) => {
          this.switchRestaurantEntityType(response);
        });
      },
    );
  };

  handleViewMenuClick = () => {
    const { selectedTable } = this.props;
    if (!isEmpty(selectedTable)) {
      const { scanCode } = selectedTable;
      this.props.history.push(`/${routes.containers.restaurant}/${scanCode}`);
    } else {
      this.setState({
        showOrderModePopup: true,
      });
    }
  };

  handleRestaurantSelect = (restaurant) => {
    const { includeMenu } = this.state;
    const { restaurantCode } = restaurant;
    this.setState(
      {
        showOrderModePopup: true,
      },
      () => {
        this.props.fetchRestaurantMenuAction(restaurantCode, includeMenu);
      },
    );
  };

  handleServiceModeClick = (service) => {
    const { code, scanCode } = service;
    let restaurantIdAndTableId = scanCode;
    if (dineInModes.includes(code)) {
      this.setState({
        showOrderModePopup: false,
        showScanQRComponent: true,
      });
    } else {
      this.props.history.push(`/${routes.containers.restaurant}/${restaurantIdAndTableId}`);
    }
  };

  onCloseOrderModePopup = () => {
    this.setState({
      showOrderModePopup: false,
    });
  };

  onCloseExceptionComponent = () => {
    this.setState({
      showErrorPopup: false,
    });
  };

  handleCloseScanQR = () => {
    this.setState({
      showScanQRComponent: false,
    });
  };

  render() {
    const {
      restaurantGroup,
      serviceTypes,
      history,
      restaurantMode,
      appSettings,
      greetingSplashScreen,
    } = this.props;

    const {
      showRestaurantSelectionScreen,
      showRestaurantWelcomeScreen,
      showOrderModePopup,
      showErrorPopup,
      errorContent,
      showScanQRComponent,
    } = this.state;

    const welcomeDoodle = get(appSettings, 'client.doodle.welcome', '');

    return (
      <div className="welcome-screen">
        {/* render greeting splash screen here */}
        {greetingSplashScreen && greetingSplashScreen}
        {/* place holder for welcome doodle */}
        {welcomeDoodle && <ImageHybrid classes="welcome-screen_doodle" sourceUrl={welcomeDoodle} />}

        {showScanQRComponent && (
          <ScanQR
            history={history}
            handleCloseScanQR={this.handleCloseScanQR}
            switchRestaurantEntityType={(response) => this.switchRestaurantEntityType(response)}
          />
        )}
        {showRestaurantSelectionScreen && (
          <RestaurantSelectionScreen
            restaurantGroup={restaurantGroup}
            handleRestaurantSelect={(restaurant) => this.handleRestaurantSelect(restaurant)}
            history={history}
          />
        )}
        {showRestaurantWelcomeScreen && (
          <RestaurantWelcomeScreen
            handleSwitchServicesClick={(scanCode) => this.handleSwitchServicesClick(scanCode)}
            handleViewMenuClick={this.handleViewMenuClick}
            history={history}
          />
        )}
        {showOrderModePopup && (
          <OrderModePopup
            openSegmentPopup={showOrderModePopup}
            dismissSegmentPopup={this.onCloseOrderModePopup}
            restaurantMode={restaurantMode}
            serviceTypes={serviceTypes}
            handleServiceModeClick={(service) => this.handleServiceModeClick(service)}
          />
        )}
        {showErrorPopup && (
          <ExceptionComponent
            pageType="HalfSize"
            openSegmentPopup={showErrorPopup}
            dismissSegmentPopup={this.onCloseExceptionComponent}
            title={get(errorContent, 'title')}
            description={get(errorContent, 'description')}
            buttonCta={get(errorContent, 'buttonCta')}
            handleButtonCta={this.onCloseExceptionComponent}
          />
        )}
      </div>
    );
  }
}

export default connect(
  (state) => ({
    restaurantGroup: get(state, 'app.restaurantGroup', {}),
    serviceTypes: get(state, 'app.serviceTypes', []),
    selectedTable: get(state, 'app.selectedTable', {}),
    restaurant: selectRestaurant(state),
    errorResponse: get(state, 'app.errorResponse', {}),
    restaurantMode: get(state, 'app.restaurant.restaurantConfig.mode', ''),
    appSettings: get(state, 'app.masterData.appSettings', {}),
  }),
  {
    fetchRestaurantMenuAction,
    clearSelectedTable,
    dispatchPreviousSelectedTableAction,
    dispatchVerifiedLocalitiesAction,
    dispatchSelectedAddressAction,
    clearApiErrorResponseAction,
    dispatchUserCart: dispatchUserCartAction,
  },
)(WelcomeScreen);
